import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import {
  KG_PER_LB,
  LITRES_PER_USG,
  formatUnit,
  loadForAircraft,
  loadWarnings,
  unitLabel,
  type LoadDataInput,
  type LoadWarning,
  type LoadingStation,
  type MassAndCg,
  type WeightBalance,
  type WeightBalanceAircraft,
} from '@jupi/shared'
import { EnvelopeChart, RemoteContent, Title, UnitSystemToggle, useUnitSystem } from '@jupi/ui'
import SaveStateChip from './SaveStateChip'
import WbAssumptionsCard from './WbAssumptionsCard'
import ConversionFactorsCard from './ConversionFactorsCard'
import WeightLadderCard from './WeightLadderCard'
import LoadingLayout from './LoadingLayout'
import { useWorkedWeight } from './useWorkedWeight'
import { usePlanDraft } from './usePlanDraft'

// Each loading station's name, as the aircraft's arms are labelled.
const STATION_LABEL: Record<LoadingStation, string> = {
  empty: 'armBasicMass',
  front: 'armFrontPax',
  rear: 'armRearPax',
  cargo: 'armCargo',
  fuelMain: 'armFuelMain',
  fuelRes: 'armFuelRes',
}

// Weight & balance for the plan's aircraft (webplan's weight and result
// pages on one screen). Big numeric inputs for a thumb; the answer
// recomputes as each figure is typed, and landing is after the plan's trip
// fuel has burned. The fuel plan itself is on its own page (FuelPage).
export default function WeightPage() {
  const { t } = useTranslation()
  const editor = usePlanDraft()
  const { load, figures, aircraft, draft } = editor
  const weight = useWorkedWeight(editor)
  const wbAircraft = aircraft?.performance ? { ...aircraft.performance, fuelType: aircraft.fuelType, mtowKg: aircraft.mtowKg } : null
  const { updateLoad } = editor

  // A different aircraft: clear what it has no room for (a rear row, a
  // baggage area) so the saved load matches the layout on screen.
  useEffect(() => {
    if (!load || !wbAircraft) return
    const fitted = loadForAircraft(wbAircraft, { ...load, baggageKg: load.baggageKg })
    const changed =
      fitted.rearKg !== load.rearKg ||
      fitted.cargoKg !== load.cargoKg ||
      (fitted.baggageKg?.length ?? 0) !== (load.baggageKg?.length ?? 0) ||
      (fitted.baggageKg ?? []).some((v, i) => v !== (load.baggageKg?.[i] ?? 0))
    if (changed) updateLoad({ rearKg: fitted.rearKg, cargoKg: fitted.cargoKg, baggageKg: fitted.baggageKg })
    // wbAircraft is rebuilt each render; the aircraft's registration decides
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, aircraft?.registration, aircraft?.performance])
  const { worked } = weight

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title label={draft?.registration
            ? `${t('nav.planWeight')} · ${draft.registration}${aircraft?.aircraftType ? ` (${aircraft.aircraftType})` : ''}`
            : t('nav.planWeight')} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {draft?.registration && aircraft?.performance ? (
            <Button
              component={RouterLink}
              to="/plan/weight/worked"
              variant="outlined"
              size="small"
              sx={{ minHeight: 32, height: 32, py: 0 }}
              startIcon={<Icon icon="mdi:calculator-variant-outline" />}
            >
              {t('plan.worked.show')}
            </Button>
          ) : null}
          <SaveStateChip state={editor.saveState} />
        </Stack>
      </Stack>
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {load && figures ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <LoadCard
                  load={load}
                  aircraft={wbAircraft}
                  onChange={editor.updateLoad}
                />
                <WbAssumptionsCard weight={weight} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2}>
                {!draft?.registration || !aircraft?.performance ? (
                  <Alert severity="info">{t('plan.weight.noPerformance')}</Alert>
                ) : worked ? (
                  <>
                    <ResultCard
                      wb={worked}
                    envelope={aircraft.performance.cgEnvelope}
                      mtowKg={aircraft.mtowKg}
                      registration={aircraft.registration}
                    />
                    <WeightLadderCard worked={worked} />
                    <ConversionFactorsCard density={worked.assumptions.density} />
                  </>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        ) : null}
      </RemoteContent>
    </Box>
  )
}

// One quantity in two units, each editable: typing in either sets the
// stored (base) figure and the other follows. `perBase` is how many of the
// second unit make one of the base unit.
function DualField({
  label,
  value,
  base,
  baseLabel = base,
  second,
  perBase,
  onChange,
}: {
  label: string
  value: number
  base: string
  /** How the base unit reads in the field, where it is not its symbol ("Litres" for l). */
  baseLabel?: string
  second: string
  perBase: number
  onChange: (n: number) => void
}) {
  const field = (unit: string, factor: number, name = unit) => (
    <UnitInput
      label={name}
      unit={name}
      value={Math.round(value * factor * 10) / 10}
      onChange={(n) => onChange(n / factor)}
    />
  )
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
        {field(base, 1, baseLabel)}
        {field(second, perBase)}
      </Box>
    </Box>
  )
}

function UnitInput({ label, value, onChange, unit }: { label: string; value: number; onChange: (n: number) => void; unit: string }) {
  // The typed text while the field has focus; otherwise the figure itself,
  // which follows the other unit's field.
  const [text, setText] = useState<string | null>(null)
  return (
    <TextField
      fullWidth
      label={label}
      value={text ?? (value ? String(value) : '')}
      placeholder="0"
      onFocus={(e) => e.target.select()}
      onBlur={() => setText(null)}
      onChange={(e) => {
        setText(e.target.value)
        const n = Number(e.target.value.replace(',', '.'))
        if (e.target.value.trim() === '') onChange(0)
        else if (Number.isFinite(n) && n >= 0 && n <= 20000) onChange(n)
      }}
      slotProps={{
        htmlInput: { inputMode: 'decimal', enterKeyHint: 'next', style: { fontSize: '1.25rem' } },
        input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> },
      }}
    />
  )
}

function LoadCard({
  load,
  aircraft,
  onChange,
}: {
  load: LoadDataInput
  aircraft: WeightBalanceAircraft | null
  onChange: (change: Partial<LoadDataInput>) => void
}) {
  const { t } = useTranslation()
  // Fuel is kept in litres from here on; a plan saved in US gallons is shown
  // converted.
  const litres = load.fuelUnit === 'usg' ? load.fuelMain * LITRES_PER_USG : load.fuelMain
  const mass = (label: string, key: 'frontKg' | 'rearKg' | 'cargoKg') => (
    <DualField label={label} value={load[key]} base="kg" second="lb" perBase={1 / KG_PER_LB} onChange={(n) => onChange({ [key]: n })} />
  )
  const warnings = aircraft ? loadWarnings(aircraft, load) : []
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('plan.weight.load')}
        </Typography>
        <Stack spacing={2}>
          {aircraft ? (
            <LoadingLayout aircraft={aircraft} load={load} warnings={warnings} onChange={onChange} />
          ) : (
            <>
              {mass(t('performance.armFrontPax'), 'frontKg')}
              {mass(t('performance.armRearPax'), 'rearKg')}
              {mass(t('performance.armCargo'), 'cargoKg')}
            </>
          )}
          <LoadWarnings warnings={warnings} />
          <DualField
            label={t('plan.weight.fuelMain')}
            value={litres}
            base="l"
            baseLabel={t('plan.weight.litres').replace(/^./, (c) => c.toUpperCase())}
            second="USG"
            perBase={1 / LITRES_PER_USG}
            onChange={(n) => onChange({ fuelUnit: 'l', fuelMain: n, fuelRes: 0 })}
          />
        </Stack>
      </CardContent>
    </Card>
  )
}

const fuelText = (usg: number) => `${(usg * LITRES_PER_USG).toFixed(0)} l / ${usg.toFixed(1)} USG`

// Every loading limit the load exceeds, named: a baggage area, the areas
// together, a tank, or weight on a seat the aircraft does not have.
function LoadWarnings({ warnings }: { warnings: LoadWarning[] }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  const mass = (kg: number) => `${formatUnit('mass', kg, system)} ${unitLabel('mass', system)}`
  if (warnings.length === 0) return null
  return (
    <Stack spacing={1}>
      {warnings.map((w) => (
        <Alert severity="error" key={w.code === 'baggageArea' ? `${w.code}${w.index}` : w.code}>
          {w.code === 'baggageArea'
            ? t('plan.weight.warn.baggageArea', { label: w.label, kg: mass(w.kg), max: mass(w.maxKg) })
            : w.code === 'baggageTotal'
              ? t('plan.weight.warn.baggageTotal', { kg: mass(w.kg), max: mass(w.maxKg) })
              : w.code === 'rearSeats'
                ? t('plan.weight.warn.rearSeats')
                : t(`plan.weight.warn.${w.code}`, { amount: fuelText(w.usg), max: fuelText(w.maxUsg) })}
        </Alert>
      ))}
    </Stack>
  )
}

function StateLine({ label, state }: { label: string; state: MassAndCg }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  const ok = state.withinEnvelope && state.withinMtow
  return (
    <Box sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: ok ? 'success.main' : 'error.main' }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Chip size="small" color={ok ? 'success' : 'error'} label={ok ? 'OK' : '!'} />
      </Stack>
      <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
        {formatUnit('mass', state.kg, system, 0)} {unitLabel('mass', system)} · {t('plan.weight.cg')}{' '}
        {state.cgArm !== null ? `${formatUnit('arm', state.cgArm, system)} ${unitLabel('arm', system)}` : '–'}
      </Typography>
      {!state.withinMtow ? (
        <Typography variant="body2" color="error">
          {t('plan.weight.overMtow')}
        </Typography>
      ) : null}
      {!state.withinEnvelope ? (
        <Typography variant="body2" color="error">
          {t('plan.weight.outsideEnvelope')}
        </Typography>
      ) : null}
    </Box>
  )
}

function ResultCard({
  wb,
  envelope,
  mtowKg,
  registration,
}: {
  wb: WeightBalance
  envelope: { arm: number; kg: number }[]
  mtowKg: number | null
  registration: string
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const system = useUnitSystem()
  return (
    <Card>
      <CardContent>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
          <Typography variant="h6">{t('plan.weight.sheet')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {registration}
            {mtowKg ? ` · ${t('plan.weight.mtow')} ${Number(formatUnit('mass', mtowKg, system))} ${unitLabel('mass', system)}` : ''}
          </Typography>
        </Stack>
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
          <StateLine label={t('plan.weight.takeoff')} state={wb.takeoff} />
          <StateLine label={t('plan.weight.landing')} state={wb.landing} />
        </Box>
        <EnvelopeChart
          bothUnits
          action={<UnitSystemToggle compact />}
          envelope={envelope}
          markers={[
            ...(wb.takeoff.cgArm !== null
              ? [{ label: t('plan.weight.takeoff'), arm: wb.takeoff.cgArm, kg: wb.takeoff.kg, color: theme.palette.error.main }]
              : []),
            ...(wb.landing.cgArm !== null
              ? [{ label: t('plan.weight.landing'), arm: wb.landing.cgArm, kg: wb.landing.kg, color: theme.palette.primary.main }]
              : []),
          ]}
        />
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }}>{t('plan.weight.station')}</TableCell>
              <TableCell align="right">{t('units.mass', { unit: unitLabel('mass', system) })}</TableCell>
              <TableCell align="right">{t('units.arm', { unit: unitLabel('arm', system) })}</TableCell>
              <TableCell align="right" sx={{ pr: 0 }}>
                {t('units.moment', { unit: unitLabel('moment', system) })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wb.rows
              .filter((row) => row.station === 'empty' || row.kg > 0)
              .map((row) => (
                <TableRow key={row.station}>
                  <TableCell sx={{ pl: 0 }}>{t(`performance.${STATION_LABEL[row.station]}`)}</TableCell>
                  <TableCell align="right">{formatUnit('mass', row.kg, system)}</TableCell>
                  <TableCell align="right">{formatUnit('arm', row.arm, system)}</TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    {formatUnit('moment', row.moment, system)}
                  </TableCell>
                </TableRow>
              ))}
            <TableRow>
              <TableCell sx={{ pl: 0, fontWeight: 700 }}>{t('plan.editor.totals')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatUnit('mass', wb.ramp.kg, system)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {wb.ramp.cgArm !== null ? formatUnit('arm', wb.ramp.cgArm, system) : '–'}
              </TableCell>
              <TableCell align="right" sx={{ pr: 0, fontWeight: 700 }}>
                {formatUnit('moment', wb.ramp.moment, system)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
